import {Icon, Button, Tag, Text, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {add} from "date-fns";
import {useCallback, useEffect, useState} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  SearchableDispatchGroupSortableFields,
  SearchableSortDirection,
  useSearchDispatchGroupsQuery,
  VehicleType
} from "../../../generated/graphql";
import {extractSimplePref, usePreferenceContext} from "../../../providers/PreferenceProvider";
import {getKeysFromArrayOfObjects, isSameDate} from "../../../utils/General";
import {Constants, EmptyValueStrings, TenantPreferences} from "../../../constants/constants";
import {ManifestStatusType} from "../../settings/ColorSettings/ColorizedIndicators/types/manifestAttributes";
import DispatchGroupsSeletor, {
  ClearInputButton,
  TChangedDispatchGroupState,
  TGroupOption
} from "../common/components/DispatchGroupsSeletor";
import {ManifestModalDialog} from "../common/components/ManifestModalDialog";
import {CalendarIcon, InformationIcon} from "../../common/icons";
import CreateManifestDatePicker, {formatDate} from "./components/CreateManifestDatePicker";
import DriverItem from "./components/DriverItem";
import {TDriverEnhanced} from "../../common/types/enhanced";

export type TDriverItem = TDriverEnhanced & {manifestDriverStatus?: ManifestStatusType};

interface ICreateManifestModalProps {
  vehicleTypes: VehicleType[];
  options: TDriverEnhanced[];
  selectedOptions: TDriverItem[];
  onOptionSelect: (item: TDriverItem) => void;
  onCreateManifest: (date: Date) => void;
  onSearchDriver: (query: string) => void;
  onClear: () => void;
  onClose: () => void;
  onLoadMore: () => void;
}

type TSelectedItemProps = {
  item: TDriverItem;
  handleClick: (item: TDriverItem) => void;
};

const today = new Date();
const tomorrow = add(today, {
  days: 1
});

const getSelectedDatePrefixText = (selectedDate: Date) => {
  if (isSameDate(today, selectedDate)) return "Today, ";
  if (isSameDate(tomorrow, selectedDate)) return "Tomorrow, ";
  return "";
};

const CreateManifestModal = ({
  vehicleTypes,
  options,
  selectedOptions,
  onOptionSelect,
  onCreateManifest,
  onSearchDriver,
  onClear,
  onClose,
  onLoadMore
}: ICreateManifestModalProps) => {
  const [dispatchGroupOptions, setDispatchGroupOptions] = useState<TGroupOption[]>([]);
  const [isSelectingDate, setIsSelectingDate] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedGroup, setSelectedGroup] = useState<TGroupOption | undefined>(undefined);
  const [editingDriver, setEditingDriver] = useState<TDriverEnhanced | undefined>(undefined);
  const [changedDispatchGroupState, setChangedDispatchGroupState] = useState<TChangedDispatchGroupState[]>([]);
  const [changedStatusState, setChangedStatusState] = useState<Map<number, ManifestStatusType>>();
  const [changedVehicleState, setChangedVehicleState] = useState<Map<number, number>>();
  const {tenantPreferences} = usePreferenceContext();

  const defaultManifestStatus = extractSimplePref(
    tenantPreferences,
    TenantPreferences.defaultManifestStatus,
    ManifestStatusType.Active
  ).value as ManifestStatusType;

  const [query, setQuery] = useState<string>("");

  useSearchDispatchGroupsQuery({
    variables: {
      filter: {
        dispatchType: {
          eq: "D"
        }
      },
      sort: {
        field: SearchableDispatchGroupSortableFields.DispatchGroup,
        direction: SearchableSortDirection.Asc
      },
      limit: Constants.DISPATCH_GROUP_LIMIT
    },
    onCompleted(data) {
      if (data.searchDispatchGroups?.items) {
        const groupOptions: TGroupOption[] = data.searchDispatchGroups.items.map((item) => {
          return {key: item?.dispatchGroupId as number, value: item?.dispatchGroup ?? EmptyValueStrings.unknown};
        });
        setDispatchGroupOptions(groupOptions);
      }
    }
  });

  const onChangeFilter = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setQuery(query);
  }, []);

  const onSelectDriverHandler = useCallback(
    (item: TDriverItem) => {
      onOptionSelect(item);
      setEditingDriver(undefined);
      setQuery("");
    },
    [onOptionSelect]
  );

  const onEditClickHandler = useCallback(
    (driver: TDriverEnhanced) => {
      if (editingDriver?.driverId !== driver.driverId) {
        setEditingDriver(driver);
      } else {
        setEditingDriver(undefined);
      }
      setSelectedGroup(undefined);
    },
    [editingDriver]
  );

  const onChangeStatusHandler = useCallback(
    (driverId: number, status: ManifestStatusType) => {
      const newState = new Map(changedStatusState);
      newState.set(driverId, status);
      setChangedStatusState(newState);
    },
    [changedStatusState]
  );

  const onVehicleSelectHandler = useCallback(
    (driverId: number, vehicleId: number) => {
      const newState = new Map(changedVehicleState);
      newState.set(driverId, vehicleId);
      setChangedVehicleState(newState);
    },
    [changedVehicleState]
  );

  const renderOptionList = useCallback(
    (options: TDriverEnhanced[]) => {
      const selectedDriverIds = getKeysFromArrayOfObjects(selectedOptions, "driverId");
      const filteredOptions = options.filter((option) => !selectedDriverIds.includes(option.driverId));
      if (filteredOptions.length === 0) {
        return <div style={{textAlign: "center", fontSize: "14px", fontStyle: "italic"}}>No driver found!</div>;
      }
      return filteredOptions.map((option, index) => {
        const {driverId, vehicleTypeId} = option;
        const changedDGState = changedDispatchGroupState.find((item) => item.driverId === driverId);
        const selectedStatus = changedStatusState?.get(driverId!) || defaultManifestStatus;
        const selectedVehicleId = changedVehicleState?.get(driverId!) || vehicleTypeId;
        const isHighlight = editingDriver?.driverId === driverId;
        return (
          <DriverItem
            key={driverId}
            index={index}
            item={option}
            vehicleTypes={vehicleTypes}
            isHighlight={isHighlight}
            onClick={onSelectDriverHandler}
            onEditClick={onEditClickHandler}
            changedDispatchGroupState={changedDGState}
            onStatusSelect={onChangeStatusHandler}
            selectedStatus={selectedStatus}
            onVehicleSelect={onVehicleSelectHandler}
            selectedVehicleId={selectedVehicleId ?? null}
          />
        );
      });
    },
    [
      changedDispatchGroupState,
      changedStatusState,
      changedVehicleState,
      defaultManifestStatus,
      editingDriver?.driverId,
      onChangeStatusHandler,
      onEditClickHandler,
      onSelectDriverHandler,
      onVehicleSelectHandler,
      selectedOptions,
      vehicleTypes
    ]
  );

  const renderTagList = useCallback(
    (options: TDriverItem[]) => {
      return options.map((option) => {
        return <ItemTag key={option.driverId} item={option} handleClick={onOptionSelect} />;
      });
    },
    [onOptionSelect]
  );

  const changeDateHanlder = useCallback((date: Date | undefined, isUserChange: boolean) => {
    if (!date) {
      setIsSelectingDate(false);
      return;
    }
    if (isUserChange) {
      setSelectedDate(date);
      setIsSelectingDate(false);
    }
  }, []);

  const createManifestHandler = useCallback(() => {
    if (selectedDate) {
      onCreateManifest(selectedDate);
    }
  }, [onCreateManifest, selectedDate]);

  const clearHandler = useCallback(() => {
    setQuery("");
    onClear();
  }, [onClear]);

  //Change dispatch group handlers
  const onSelectGroupHandler = useCallback((group: TGroupOption) => {
    setSelectedGroup(group);
  }, []);

  const onCancelEditGroupHandler = useCallback(() => {
    setSelectedGroup(undefined);
    setEditingDriver(undefined);
  }, []);

  const onConfirmEditGroupHandler = useCallback(() => {
    if (editingDriver?.dispatchGroupId?.toString() !== selectedGroup?.key.toString()) {
      setChangedDispatchGroupState((prev) => [
        ...prev.filter((item) => item.driverId !== editingDriver?.driverId),
        {...selectedGroup!, driverId: editingDriver?.driverId as number}
      ]);
    } else {
      setChangedDispatchGroupState((prev) => prev.filter((item) => item.driverId !== editingDriver?.driverId));
    }
    onCancelEditGroupHandler();
  }, [editingDriver?.dispatchGroupId, editingDriver?.driverId, onCancelEditGroupHandler, selectedGroup]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchDriver(query);
      if (editingDriver) onCancelEditGroupHandler();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <ManifestModalDialog id="create-manifest" onClose={onClose}>
      {isSelectingDate ? (
        <CreateManifestDatePicker onChangeDate={changeDateHanlder} selectedDate={selectedDate} />
      ) : (
        <Container>
          <Header>
            <Title>Select driver(s) for</Title>
            {selectedDate && (
              <SelectedDateText>
                {getSelectedDatePrefixText(selectedDate)}
                {selectedDate.toLocaleString("US", formatDate)}
              </SelectedDateText>
            )}
          </Header>
          <Body>
            <SearchContainer>
              <Search>
                <StyledIcon icon="search" />
                <StyledInput type="text" placeholder="Search by Name or ID" value={query} onChange={onChangeFilter} />
                {query && <ClearInputButton icon="cross" minimal onClick={() => setQuery("")} />}
              </Search>
              <StyledCalendarButton
                data-testid="calendar-button"
                onClick={() => setIsSelectingDate(true)}
                endIcon={<CalendarIcon />}
              />
            </SearchContainer>
            <SelectedItemsContainer>{renderTagList(selectedOptions)}</SelectedItemsContainer>
            <InfoContainer>
              <Tooltip
                popoverClassName="content-tooltip"
                content={
                  "The default status of the manifest is defined in the Dispatch company settings by your admin(s). You can adjust the status of the manifest in this flow or after the manifest is created."
                }
                placement="top"
                intent="none"
              >
                <InformationIcon scale={0.6} />
              </Tooltip>
            </InfoContainer>
            <ItemsContainer
              hasSelectedOptions={selectedOptions.length > 0}
              id={"option-infinity-scroll-list"}
              data-testid="drivers-option-container"
            >
              <InfiniteScroll
                next={onLoadMore}
                dataLength={options.length}
                hasMore={true}
                scrollableTarget={"option-infinity-scroll-list"}
                loader={""}
              >
                <OptionList>{renderOptionList(options)}</OptionList>
              </InfiniteScroll>
            </ItemsContainer>
          </Body>
          <Footer>
            <ClearButton
              data-testid="clear-selected-driver-button"
              onClick={clearHandler}
              disabled={selectedOptions.length === 0}
            >
              Clear{selectedOptions.length ? <span style={{color: "red"}}> ({selectedOptions.length})</span> : ""}
            </ClearButton>
            <CreateButton onClick={createManifestHandler} disabled={selectedOptions.length === 0}>
              Create
            </CreateButton>
          </Footer>
          {editingDriver && (
            <GroupSelectContainer>
              <DispatchGroupsSeletor
                initGroup={{
                  key: Number(editingDriver.dispatchGroupId),
                  value: editingDriver.dispatchGroup?.dispatchGroup ?? EmptyValueStrings.unknown
                }}
                options={dispatchGroupOptions}
                selectedGroup={selectedGroup}
                onSelectGroup={onSelectGroupHandler}
                onConfirm={onConfirmEditGroupHandler}
                onCancel={onCancelEditGroupHandler}
                changedDispatchGroupState={changedDispatchGroupState.find(
                  (item) => item.driverId === editingDriver.driverId
                )}
              />
            </GroupSelectContainer>
          )}
        </Container>
      )}
    </ManifestModalDialog>
  );
};

export default CreateManifestModal;

const ItemTag = ({item, handleClick}: TSelectedItemProps) => {
  return (
    <StyledTag
      data-testid="selected-item"
      style={{margin: "1.5px"}}
      key={item.driverId}
      endIcon={
        <Icon
          data-testid="deselect-item-button"
          style={{cursor: "pointer"}}
          icon="cross"
          size={16}
          onClick={() => handleClick(item)}
        />
      }
    >
      <Text ellipsize>{item.name ? item.name : EmptyValueStrings.unknown}</Text>
    </StyledTag>
  );
};

const SearchContainer = styled.div`
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
`;

const SelectedItemsContainer = styled.div`
  padding: 0 20px;
  max-height: 90px;
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  margin-bottom: 8px;
`;

const ItemsContainer = styled.div<{hasSelectedOptions?: boolean}>`
  height: ${(props) => (props.hasSelectedOptions ? "calc(100% - 162px)" : "calc(100% - 62px)")};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const Header = styled.div`
  gap: 4px;
  display: flex;
  padding: 10px 20px 0;
`;

const Title = styled.div`
  font-weight: 400;
  text-transform: uppercase;
`;

const SelectedDateText = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #797979;
`;

const Container = styled.div`
  position: relative;
  width: 512px;
  height: 70vh;
  display: grid;
  grid-template-rows: 38px 1fr 62px;
`;

const Body = styled.div`
  height: calc(70vh - 100px);
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
`;

const InfoContainer = styled.div`
  height: 22px;
  margin-left: 18px;
`;

const GroupSelectContainer = styled.div`
  position: absolute;
  height: 70vh;
  width: 190px;
  top: 0;
  right: -190px;
  background: #fff;
  display: flex;
  overflow: hidden;
`;

const OptionList = styled.ul`
  list-style: none;
  text-overflow: ellipsis;
  padding: 0;
  margin: 0;
`;

const StyledTag = styled(Tag)`
  background-color: #8da4beb2;
  font-size: 12px;
  border-radius: 4px;
`;

const Search = styled.div`
  width: 100%;
  font-family: "Roboto", sans-serif;
  position: relative;
  border-radius: 4px;
  display: flex;
  height: 32px;
  align-items: center;
  background-color: #ffffff;
  box-shadow: 0px 1px 3px 0px #0000001a inset;
  border: 1px solid #c3c3c373;
`;

const StyledIcon = styled(Icon)`
  color: #a4a5a6;
  padding: 8px 8px 8px 12px;
`;

const StyledInput = styled.input`
  display: inline;
  width: 100%;
  line-height: 16px;
  border: none;
  outline: none;
`;

const ClearButton = styled.button`
  font-size: 16px;
  font-weight: 400;
  line-height: 14px;
  border: none;
  color: #14305a;
  background-color: transparent;
  cursor: pointer;
  padding: 0;

  span {
    color: #fa545e;
  }

  :disabled {
    color: #8da4be;
  }
`;

const CreateButton = styled(Button)`
  &.bp5-button:not([class*="bp5-intent-"]) {
    color: #ffffff;
    background-color: "#14305a";
    background: linear-gradient(0deg, rgba(15, 18, 23, 0.1), rgba(15, 18, 23, 0.1)),
      linear-gradient(0deg, #14305a, #14305a) !important;
    border: 1px solid #d9d9d9 !important;
    box-shadow: 0px 1px 2px 0px #0000000d !important;
  }

  &.bp5-button:not([class*="bp5-intent-"]):disabled {
    color: #fbfbfb;
    background-color: #d9d9d9;
    background: linear-gradient(0deg, rgba(15, 18, 23, 0.1), rgba(15, 18, 23, 0.1)),
      linear-gradient(0deg, #d9d9d9, #d9d9d9) !important;
    border: 1px solid #d9d9d9 !important;
    box-shadow: 0px 1px 2px 0px #0000000d !important;
  }
  font-size: 16px;
  font-weight: 400;
  border-radius: 4px;
  height: 32px;
  padding: 6px 14px;
  border: 1px;
  gap: 7px;
`;

const StyledCalendarButton = styled(Button)`
  &.bp5-button:not([class*="bp5-intent-"]) {
    color: #ffffff;
    background-color: #ffffff;
    border: 1px solid #d9d9d9 !important;
    box-shadow: 0px 1px 1px 0px #00000040 !important;
    border-radius: 4px;
  }
`;
